import { useCallback, useEffect, useState } from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  DETAIL_PANE_MAX_WIDTH,
  FeedbackForm,
  LANGUAGE_REGION_KEY,
  SettingsMenuView,
  languageRegionSection,
  resolveTheme,
  useBreakpoint,
  useMasterDetailSlots,
  type SettingsMenuSection,
  type UiTheme,
} from "@heraion/ui";
import { PANE_FLEX } from "./shell/appShellStyles";
import { useThemePalette } from "./theme";
import { CatalogScreen } from "./CatalogScreen";
import { LanguageRegionScreen } from "./LanguageRegionScreen";
import { PreferencesScreen } from "./PreferencesScreen";
import { BodyScreen } from "./BodyScreen";
import { AccountScreen } from "./AccountScreen";
import { EffortScreen } from "./EffortScreen";
import { TimersScreen } from "./TimersScreen";
import { checkAndRecord } from "./feedbackRateLimit";
import { submitWebFeedback } from "./submitWebFeedback";

// Web-rnw Settings surface — the console mirror of mobile's Settings tab,
// SAME IA (Settings → sub-screen, never a top-level tab per lift). The menu
// itself is the shared SettingsMenuView; only the navigation differs by
// viewport (the lift seam):
//   wide   → master-detail (menu list pane left, the selected sub-screen
//            fills the right pane; the active row stays highlighted).
//   narrow → menu → in-place sub-screen with a back row (state push), the
//            phone experience.
// Settings is app config only. MAXes / Measurements moved to the Me
// avatar-menu (ME-1 / MS-1), the pain map and Coach & athletes followed with
// #315: the athlete's own state, not app config, mirroring the mobile Me tab.
// Effort / Timers came back the other way (#315): they say how the app
// behaves. Account / sign-out reach from here and from the avatar-menu.

const LIST_PANE_WIDTH = 232;

export type SettingsSubKey = "languageRegion" | "account" | "catalog" | "effort" | "timers" | "body" | "prefs";
type SubKey = SettingsSubKey;

/** A sub-screen asked for from outside (the bell, #178): the shell has no
 *  router, so it hands the ask in as a prop and `onRequestTaken` clears it
 *  once it is read. `focusName`: the Account name field takes focus. */
export type SettingsRequest = { sub: SettingsSubKey; focusName?: boolean };

export function SettingsScreen({
  request = null,
  onRequestTaken,
}: {
  request?: SettingsRequest | null;
  onRequestTaken?: () => void;
}) {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t, i18n } = useTranslation();
  const { wide } = useBreakpoint();
  // Opens on the asked sub-screen from the first render (no flash of the menu).
  const [sub, setSub] = useState<SubKey | null>(request?.sub ?? null);
  const [focusName, setFocusName] = useState(request?.focusName === true);
  // The ask, taken once: when Settings is already open the effect moves it.
  useEffect(() => {
    if (request === null) return;
    setSub(request.sub);
    setFocusName(request.focusName === true);
    onRequestTaken?.();
  }, [request, onRequestTaken]);
  const nameFocusTaken = useCallback(() => setFocusName(false), []);
  // Feedback is not a sub-screen: its row opens the shared form as an overlay
  // (same IA as mobile's Settings -> Feedback), so it has no SettingsSubKey.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const styles = makeStyles(theme);

  const sections: SettingsMenuSection[] = [
    // #390: first, with a globe, so a wrong language can be found again.
    languageRegionSection(
      t("settings.langRegion.title", { defaultValue: "Language & region" }),
      i18n.language,
    ),
    {
      label: t("mobile.settings.sectionAccount", { defaultValue: "Account" }),
      rows: [
        {
          key: "account",
          title: t("mobile.account.title", { defaultValue: "Account" }),
          sub: t("mobile.account.navSub", {
            defaultValue: "Name, password, export, delete, sign out",
          }),
        },
      ],
    },
    {
      // MAXes moved to Me (ME-1); Effort / Timers came back from Me with
      // #315, as on mobile. Where the catalog goes is #381.
      label: t("mobile.settings.sectionTraining", { defaultValue: "Training" }),
      rows: [
        {
          key: "catalog",
          title: t("mobile.catalog.title", { defaultValue: "Catalog" }),
          sub: t("mobile.profile.navCatalogSub", {
            defaultValue: "Browse exercises, records and history.",
          }),
        },
        {
          key: "effort",
          title: t("mobile.settings.navEffort", { defaultValue: "Effort settings" }),
          sub: t("mobile.settings.navEffortSub", {
            defaultValue: "RIR / RPE scale + legend",
          }),
        },
        {
          key: "timers",
          title: t("mobile.profile.navRestTimer", { defaultValue: "Timers" }),
          sub: t("mobile.profile.navRestTimerSub", {
            defaultValue: "Rest + hold defaults, sound, per-exercise",
          }),
        },
      ],
    },
    {
      // Measurements and the pain map live in Me -> Body (MS-1, #315). Sex /
      // age / figure stay here, under «Personal details» so that «Body»
      // names one place only (19/10), as on mobile.
      label: t("mobile.settings.sectionPersonalDetails", { defaultValue: "Personal details" }),
      rows: [
        {
          key: "body",
          title: t("mobile.settings.sectionPersonalDetails", { defaultValue: "Personal details" }),
          sub: t("mobile.body.navSub", { defaultValue: "Sex, age, figure" }),
        },
      ],
    },
    {
      label: t("mobile.settings.sectionPrefs", { defaultValue: "Preferences" }),
      rows: [
        {
          key: "prefs",
          title: t("mobile.prefs.title", { defaultValue: "Units & display" }),
          sub: t("mobile.prefs.navSub", {
            defaultValue: "Units, theme, hints",
          }),
        },
      ],
    },
    {
      label: t("mobile.settings.sectionAbout", { defaultValue: "About" }),
      rows: [
        {
          key: "feedback",
          title: t("feedback.title"),
          sub: t("feedback.help"),
        },
      ],
    },
  ];

  const menu = (selectedKey: string | null, dense = false) => (
    <SettingsMenuView
      theme={theme}
      sections={sections}
      selectedKey={selectedKey}
      onSelect={(key) =>
        key === "feedback" ? setFeedbackOpen(true) : setSub(key as SubKey)
      }
      dense={dense}
    />
  );

  const detail = (key: SubKey) => {
    switch (key) {
      case LANGUAGE_REGION_KEY:
        return <LanguageRegionScreen />;
      case "account":
        return <AccountScreen focusName={focusName} onFocusTaken={nameFocusTaken} />;
      case "prefs":
        return <PreferencesScreen />;
      case "body":
        return <BodyScreen />;
      case "effort":
        return <EffortScreen />;
      case "timers":
        return <TimersScreen />;
      case "catalog":
        return <CatalogScreen />;
    }
  };

  // MX-8's twin: ONE tree shape for both layouts, so `detail(sub)` (a form
  // screen the athlete may be mid-edit in) stays the same node across a
  // resize instead of being torn down by two `if (wide) return` branches that
  // put it at different positions. `sub`'s own screen only ever (re)mounts
  // when `sub` itself changes, never when `wide` does. See
  // useMasterDetailSlots for the shared three-slot shape.
  const { showList: showMenu, showBack, showDetail } = useMasterDetailSlots(
    wide,
    sub !== null,
  );
  return (
    <View style={wide ? styles.row : styles.flex}>
      {showMenu ? (
        <View style={wide ? [styles.listPane, styles.listBody] : styles.flex}>
          {wide ? (
            menu(sub, true)
          ) : (
            <ScrollView style={styles.flex} contentContainerStyle={styles.menuBody}>
              {menu(null)}
            </ScrollView>
          )}
        </View>
      ) : null}
      {showBack ? (
        <Pressable
          accessibilityRole="button"
          hitSlop={8}
          style={({ pressed }) => [styles.backRow, pressed && styles.rowPressed]}
          onPress={() => setSub(null)}
        >
          <Text style={styles.back}>
            ‹ {t("mobile.common.back", { defaultValue: "Back" })}
          </Text>
        </Pressable>
      ) : null}
      {showDetail ? (
        <View style={wide ? styles.detailPane : styles.flex}>
          {sub ? (
            // Catalog is itself master-detail → fill the pane, no extra
            // wrapper in EITHER layout (it manages its own internal scroll;
            // an ancestor flex box around it is one more place that height
            // chain can break). The single-column form surfaces get a
            // wrapper so its cap can toggle without changing the node: capped
            // on wide, the neutral PANE_FLEX (no cap) on narrow.
            sub === "catalog" ? (
              detail(sub)
            ) : (
              <View style={wide ? styles.formCap : PANE_FLEX}>{detail(sub)}</View>
            )
          ) : (
            <View style={styles.empty}>
              <Text style={styles.emptyGlyph}>‹</Text>
              <Text style={styles.emptyTitle}>
                {t("mobile.settings.selectPrompt", {
                  defaultValue: "Select a setting to open it.",
                })}
              </Text>
            </View>
          )}
        </View>
      ) : null}
      <FeedbackForm
        theme={theme}
        visible={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        submit={submitWebFeedback}
        checkRateLimit={checkAndRecord}
      />
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: p.bg },
    row: { flex: 1, flexDirection: "row" },
    // Secondary, in-content nav — sits on the page bg (NOT surface) so it
    // reads as subordinate to the app rail, never a twin chrome panel.
    listPane: {
      width: LIST_PANE_WIDTH,
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: LIST_PANE_WIDTH,
      borderRightWidth: 1,
      borderRightColor: p.border,
      backgroundColor: p.bg,
    },
    listBody: { paddingHorizontal: s.s5, paddingVertical: s.s4 },
    menuBody: { paddingHorizontal: s.s5, paddingVertical: s.s4 },
    detailPane: { flex: 1, minWidth: 0 },
    // Single-column forms: capped width, left-aligned in the detail pane.
    // Its uncapped twin is the shared PANE_FLEX (appShellStyles.ts).
    formCap: { flex: 1, width: "100%", maxWidth: DETAIL_PANE_MAX_WIDTH },
    empty: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: s.s5,
      gap: s.s3,
    },
    emptyGlyph: {
      color: p.ink3,
      fontSize: 44,
      lineHeight: 44,
      fontWeight: "300",
    },
    emptyTitle: {
      color: p.ink2,
      fontSize: t.sizes.lg,
      fontWeight: "600",
      textAlign: "center",
      maxWidth: 280,
    },
    backRow: {
      minHeight: 44,
      justifyContent: "center",
      paddingHorizontal: s.s5,
      paddingVertical: s.s3,
    },
    rowPressed: { backgroundColor: p.surface2 },
    back: { color: p.brand500, fontSize: t.sizes.base },
  });
}
